import type { AnyAction, ListenerMiddlewareInstance, ThunkDispatch } from "@reduxjs/toolkit";
import { isAnyOf } from "@reduxjs/toolkit";
import {
  UNAUTHORIZED_ERROR_NAME,
  SESSION_EXPIRED_ERROR_NAME,
  FORBIDDEN_ERROR_NAME,
  SESSION_UNAVAILABLE_ERROR_NAME,
} from "@shared-kernel/domain/errors/errors";
import { handleAuthError } from "@user-management-context/write/application/use-cases/handle-auth-error/handle-auth-error";
import { logoutUser } from "@user-management-context/write/application/use-cases/user-logout/logout-user";
import { onboardStudent } from "@user-management-context/write/application/use-cases/student-onboarding/onboard-student";
import { onboardTeacher } from "@user-management-context/write/application/use-cases/teacher-onboarding/onboard-teacher";
import { onboardProvider } from "@user-management-context/write/application/use-cases/provider-onboarding/onboard-provider";
import { loginUser } from "@user-management-context/write/application/use-cases/user-login/login-user";
import { retrieveUserProfile } from "@user-management-context/read/application/use-cases/user-profile/retrieve-user-profile";
import { syncSession } from "@user-management-context/write/application/use-cases/session/sync-session";
import { terminateSession } from "@user-management-context/write/application/use-cases/session/terminate-session";
import { revokeRefreshToken } from "@user-management-context/write/application/use-cases/user-logout/revoke-refresh-token";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { writeActiveRoleToStorage } from "@shared-kernel/core/active-role/active-role";
import { getRefreshTokenCookie } from "@utils/access-token-cookie";
import type { AppState } from "./app-state";
import type { Dependencies } from "./store";

export function addListeners(
  listenerMiddleware: ListenerMiddlewareInstance<
    AppState,
    ThunkDispatch<AppState, Partial<Dependencies>, AnyAction>,
    Partial<Dependencies>
  >,
) {
  // Global auth-error interceptor: any rejected thunk carrying an auth-related
  // error name funnels through here so each use-case doesn't reimplement the
  // recovery flow.
  listenerMiddleware.startListening({
    predicate: action => {
      const name = action?.["error"]?.name;
      if (name !== UNAUTHORIZED_ERROR_NAME && name !== SESSION_EXPIRED_ERROR_NAME && name !== FORBIDDEN_ERROR_NAME) {
        return false;
      }
      // loginUser failures are surfaced to the login form, not treated as a
      // session expiration — there is no session to recover yet.
      if (loginUser.rejected.match(action)) return false;
      // syncSession owns profile-fetch error handling; excluding the inner
      // rejection avoids recursion when the listener re-dispatches syncSession.
      if (retrieveUserProfile.rejected.match(action)) return false;
      return true;
    },
    effect: async (action, listenerApi) => {
      const errorName = action["error"].name;

      // Login timeout means the token is gone for good; skip the resync probe
      // and go straight to the redirect/cleanup flow.
      if (errorName === SESSION_EXPIRED_ERROR_NAME) {
        listenerApi.dispatch(handleAuthError({ name: errorName, message: errorName }));
        return;
      }

      // Report a refusal straight away, before the probe: syncSession is
      // single-flight, so a concurrent one may swallow this dispatch and leave us
      // without a verdict to read. The message does not depend on that verdict —
      // a 403 refuses an action whatever the session turns out to be — and the
      // session reducer ignores this one, so nothing is torn down here. Should the
      // probe then find the session dead, its own termination takes over.
      if (errorName === FORBIDDEN_ERROR_NAME) {
        listenerApi.dispatch(handleAuthError({ name: FORBIDDEN_ERROR_NAME, message: FORBIDDEN_ERROR_NAME }));
      }

      // 401s can be transient (a token just refreshed by another tab) and a 403
      // can mean a role was revoked server-side (F-022), so both resync. Termination
      // is not decided here: the syncSession listener below owns it, so the boot
      // path — where syncSession is dispatched directly — gets the same treatment.
      await listenerApi.dispatch(syncSession());
    },
  });

  // Single exit for a dead session, whoever asked for the sync: the listener
  // above (a 401 in mid-session) and the app shell at boot both land here. Left
  // to the caller, the boot path reported nothing — dead cookies survived, every
  // page load replayed 401 + 440, and the user was never told why.
  listenerMiddleware.startListening({
    actionCreator: syncSession.fulfilled,
    effect: (action, listenerApi) => {
      if (action.payload.status !== "terminated") return;
      // An anonymous visitor: nothing happened, nothing to say.
      if (action.payload.reason === "no-token") return;

      // A transient failure (rate limit, 5xx, network): the refresh token is still
      // valid, so nothing is cleared — but say so, otherwise the user lands on the
      // login screen with no explanation and assumes they were signed out.
      if (action.payload.reason === "unavailable") {
        listenerApi.dispatch(handleAuthError({ name: SESSION_UNAVAILABLE_ERROR_NAME, message: SESSION_UNAVAILABLE_ERROR_NAME }));
        return;
      }

      const name = action.payload.reason === "session-expired" ? SESSION_EXPIRED_ERROR_NAME : UNAUTHORIZED_ERROR_NAME;
      listenerApi.dispatch(handleAuthError({ name, message: name }));
    },
  });

  // Mirror successful logout to the session boundary (cookies, storage, cross-
  // tab broadcast) so all client-side auth state clears in one place.
  // Why a listener and not an extraReducer: terminateSession() clears cookies
  // — a side effect — and reducers must stay pure. The listenerMiddleware is
  // the right place for reactive side effects triggered by an action.
  listenerMiddleware.startListening({
    actionCreator: logoutUser,
    effect: async (_action, listenerApi) => {
      // Clear local state FIRST, synchronously: the caller hard-redirects
      // (window.location.href) right after dispatching, so an awaited server call
      // placed before this line loses the page — and with it the cookie cleanup.
      // The session then survives the logout and the next boot resyncs it.
      const refreshToken = getRefreshTokenCookie();
      terminateSession();

      // Then revoke the family server-side (best-effort). Done via a thunk since
      // the listener middleware has no extraArgument. The request is already sent
      // when the page unloads, so the revocation still reaches the server.
      await listenerApi.dispatch(revokeRefreshToken(refreshToken));
    },
  });

  // After onboarding, persist the chosen role per-tab and resync so the
  // session slice picks up the freshly granted role without a page reload.
  listenerMiddleware.startListening({
    matcher: isAnyOf(onboardStudent.fulfilled, onboardTeacher.fulfilled, onboardProvider.fulfilled),
    effect: async (action, listenerApi) => {
      let role: ROLES;
      if (onboardStudent.fulfilled.match(action)) role = ROLES.STUDENT;
      else if (onboardTeacher.fulfilled.match(action)) role = ROLES.TEACHER;
      else role = ROLES.PROVIDER;

      writeActiveRoleToStorage(role);
      await listenerApi.dispatch(syncSession());
    },
  });
}
